import React, { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Button, ButtonContainer, Fieldset, FormField, Input, Select } from '@vwfs-bronson/bronson-react';
import { RootState } from '../../services/redux';
import { resetNotes, setFilters, unloadCurrentConversation } from '../../services/redux/features/notes.slice';

export interface FilterValues {
  isParticipant: 'yes' | 'no' | '';
  status: MilesPosNotesIntegrationService.Components.Schemas.ConversationStatus;
  title: string;
  sortBy: 'all' | 'unread' | 'read';
  objectType: MilesPosNotesIntegrationService.Components.Schemas.ContextRepresentationsEnum | '';
  humanReadableObjectReferenceId: string;
  systemObjectId: string;
}

export const notesFiltersInitialValues: FilterValues = {
  isParticipant: '',
  status: 'OPEN',
  title: '',
  sortBy: 'all',
  humanReadableObjectReferenceId: '',
  systemObjectId: '',
  objectType: '',
};

interface FiltersProps {
  onSubmit: () => void;
  onReset: (notesFiltersInitialValues: FilterValues) => void;
  titleOptions: { value: string; label: string }[];
}

export const Filters: FunctionComponent<FiltersProps> = ({ onSubmit, onReset, titleOptions }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const filters = useSelector((state: RootState) => state.notes.filters);
  const isFetchingConversationsLoading = useSelector((state: RootState) => state.notes.isFetchingConversationsLoading);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    dispatch(unloadCurrentConversation());
    onSubmit();
  };

  const handleReset = () => {
    dispatch(resetNotes());
    onReset(notesFiltersInitialValues);
  };

  const handleChange = (
    key: keyof FilterValues,
    value: string | MilesPosNotesIntegrationService.Components.Schemas.ConversationStatus
  ) => {
    const updatedFilters = { ...filters, [key]: value };

    dispatch(setFilters(updatedFilters));
  };

  return (
    <form onSubmit={handleSubmit}>
      <Fieldset className="u-mb-small">
        <Fieldset.Row className="u-mb-small">
          <FormField labelForId="is-participant-form-field" labelText={t('filters:participant')} type="select">
            <Select
              value={filters.isParticipant}
              id="is-participant-form-field"
              onChange={(e) => handleChange('isParticipant', e.target.value)}
            >
              <Select.Item value="">{t('filters:any')}</Select.Item>
              <Select.Item value="yes">{t('filters:yes')}</Select.Item>
              <Select.Item value="no">{t('filters:no')}</Select.Item>
            </Select>
          </FormField>
        </Fieldset.Row>

        <Fieldset.Row className="u-mb-small">
          <FormField labelForId="status-form-field" labelText={t('filters:status')} type="select">
            <Select
              value={filters.status}
              id="status-form-field"
              onChange={(e) => handleChange('status', e.target.value)}
            >
              <Select.Item value="OPEN">{t('filters:open')}</Select.Item>
              <Select.Item value="CLOSE">{t('filters:closed')}</Select.Item>
            </Select>
          </FormField>
        </Fieldset.Row>

        <Fieldset.Row className="u-mb-small">
          <FormField labelForId="title-form-field" labelText={t('filters:title')} type="select">
            <Select value={filters.title} id="title-form-field" onChange={(e) => handleChange('title', e.target.value)}>
              <Select.Item value="">{t('filters:anyTitle')}</Select.Item>
              {titleOptions.map((option) => (
                <Select.Item key={option.value} value={option.value}>
                  {option.label}
                </Select.Item>
              ))}
            </Select>
          </FormField>
        </Fieldset.Row>

        <Fieldset.Row className="u-mb-small">
          <FormField labelForId="sortby-form-field" labelText={t('filters:sortBy')} type="select">
            <Select
              value={filters.sortBy}
              id="sortby-form-field"
              onChange={(e) => handleChange('sortBy', e.target.value)}
            >
              <Select.Item value="all">{t('filters:all')}</Select.Item>
              <Select.Item value="unread">{t('filters:unreadMessages')}</Select.Item>
              <Select.Item value="read">{t('filters:readMessages')}</Select.Item>
            </Select>
          </FormField>
        </Fieldset.Row>

        <Fieldset.Row className="u-mb-small">
          <FormField
            labelForId="context-representation-id-form-field"
            labelText={t('filters:object')}
            type="select"
            required={!!filters.humanReadableObjectReferenceId}
            requiredText="*"
          >
            <Select
              value={filters.objectType}
              id="context-representation-id-form-field"
              onChange={(e) => handleChange('objectType', e.target.value)}
              required={!!filters.humanReadableObjectReferenceId}
            >
              <Select.Item value="">{t('filters:anyContext')}</Select.Item>
              <Select.Item value="CREDIT_APPLICATION">{t('filters:creditApplication')}</Select.Item>
              <Select.Item value="PENDING_CONTRACT">{t('filters:pendingContract')}</Select.Item>
              <Select.Item value="CONTRACT">{t('filters:contract')}</Select.Item>
            </Select>
          </FormField>
        </Fieldset.Row>

        <Fieldset.Row className="u-mb-small">
          <FormField
            labelForId="context-representation-object-id-form-field"
            labelText={t('filters:reference')}
            type="input"
          >
            <Input
              value={filters.humanReadableObjectReferenceId}
              id="context-representation-object-id-form-field"
              onChange={(e) => handleChange('humanReadableObjectReferenceId', e.target.value)}
            />
          </FormField>
        </Fieldset.Row>
      </Fieldset>

      <ButtonContainer nav>
        <Button type="submit" disabled={isFetchingConversationsLoading}>
          {isFetchingConversationsLoading ? t('filters:applying') : t('filters:apply')}
        </Button>
        <Button type="button" secondary onClick={handleReset}>
          {t('filters:clear')}
        </Button>
      </ButtonContainer>
    </form>
  );
};
